import { useEffect, useMemo, useState, type CSSProperties, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type Konva from 'konva';
import type { EventBus } from '../../../core/events/IEventBus';
import type { Edge, Node } from '../../../core/models/scene';
import { findSubtypeForEdge } from '../../../lib/diagram/registries';
import { Logger } from '../../../utils/logger';

const canvasLogger = Logger.getLoggerWithPrefix('DiagramCanvasWithTransform');

export type InsertBetweenPickerState = {
  visible: boolean;
  position: { x: number; y: number };
  edgeId: string | null;
  midpointX: number;
  midpointY: number;
  shapeType?: 'flowchart-shape' | 'bpmn-shape';
};

export type LinkPromptState = {
  visible: boolean;
  position: { x: number; y: number };
  nodeId: string | null;
  initial: string;
};

export function useCanvasPopupsState(input: {
  shapePickerSourceSide: 'top' | 'right' | 'bottom' | 'left' | null;
  nodes: Node[];
  edges: Edge[];
  eventBus: EventBus;
  selectedIds: Set<string>;
  nodeRefs: MutableRefObject<Map<string, Konva.Node>>;
  stageRef: { current: any };
}): {
  popupStyle: CSSProperties | undefined;
  linkPrompt: LinkPromptState;
  setLinkPrompt: Dispatch<SetStateAction<LinkPromptState>>;
  insertBetweenPicker: InsertBetweenPickerState;
  setInsertBetweenPicker: Dispatch<SetStateAction<InsertBetweenPickerState>>;
} {
  const { shapePickerSourceSide, nodes, edges, eventBus, selectedIds, nodeRefs, stageRef } = input;

  const popupTransforms: Record<'top' | 'right' | 'bottom' | 'left', CSSProperties> = useMemo(
    () => ({
      top: { transform: 'translate(-50%, -100%)' },
      right: { transform: 'translate(0, -50%)' },
      bottom: { transform: 'translate(-50%, 0)' },
      left: { transform: 'translate(-100%, -50%)' },
    }),
    [],
  );
  const popupStyle = shapePickerSourceSide ? popupTransforms[shapePickerSourceSide] : undefined;

  const [linkPrompt, setLinkPrompt] = useState<LinkPromptState>({
    visible: false,
    position: { x: 0, y: 0 },
    nodeId: null,
    initial: '',
  });

  const [insertBetweenPicker, setInsertBetweenPicker] = useState<InsertBetweenPickerState>({
    visible: false,
    position: { x: 0, y: 0 },
    edgeId: null,
    midpointX: 0,
    midpointY: 0,
  });

  // Open insert-between picker on request from connector overlay
  useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent).detail as
          | { edgeId: string; midpointX: number; midpointY: number; screenX: number; screenY: number }
          | undefined;
        if (!detail) return;
        const target = edges.find((e) => e.id === detail.edgeId);
        if (!target || (target as any).type !== 'sequence_flow') return;
        const edgeAttrs = ((target as any)?.attrs || (target as any)?.props?.attrs || {}) as Record<string, unknown>;
        const sub = findSubtypeForEdge('flowchart' as any, (target as any).type, edgeAttrs);
        const isBpmnEdge =
          sub && ['bpmn_sequence', 'bpmn_default', 'bpmn_message', 'bpmn_conditional', 'bpmn_data_association'].includes(sub.id);
        const fromId = (target as any).from ?? (target as any).sourceShapeId;
        const toId = (target as any).to ?? (target as any).targetShapeId;
        const fromNode = nodes.find((n) => n.id === fromId);
        const toNode = nodes.find((n) => n.id === toId);
        const variantOf = (n: any) =>
          String(
            (n?.attrs as any)?.variant ??
              (n?.attrs as any)?.shapeType ??
              (n?.props as any)?.attrs?.variant ??
              (n?.props as any)?.attrs?.shapeType ??
              n?.name ??
              '',
          )
            .toLowerCase()
            .replace(/\s+/g, '-');
        const isBpmnNode = (n: any) => variantOf(n).startsWith('bpmn-');
        const shapeType = isBpmnEdge || (fromNode && isBpmnNode(fromNode)) || (toNode && isBpmnNode(toNode)) ? 'bpmn-shape' : 'flowchart-shape';
        setInsertBetweenPicker({
          visible: true,
          position: { x: detail.screenX, y: detail.screenY },
          edgeId: detail.edgeId,
          midpointX: detail.midpointX,
          midpointY: detail.midpointY,
          shapeType,
        });
      } catch {}
    };
    window.addEventListener('canvas:insert-between:open', handler as EventListener);
    return () => window.removeEventListener('canvas:insert-between:open', handler as EventListener);
  }, [edges, nodes]);

  // Listen for link prompt from toolbar
  useEffect(() => {
    const off = eventBus.on('canvas:link:prompt', async ({ nodeId }: { nodeId?: string }) => {
      try {
        const targetId = nodeId || Array.from(selectedIds)[0];
        if (!targetId) return;
        const node = nodes.find((n) => n.id === targetId) as any;
        if (!node) return;
        const kn = nodeRefs.current.get(targetId) as any;
        const stage = stageRef.current;
        if (!kn || !stage) return;
        const stageBox = stage.container().getBoundingClientRect();
        const scale = stage.scaleX();
        // place below shape center
        const w = node.w || (node as any).width || 60;
        const h = node.h || (node as any).height || 60;
        const cx = (node.x || 0) + w / 2;
        const cy = (node.y || 0) + h + 12; // 12px gap below
        const px = stageBox.left + stage.x() + cx * scale;
        const py = stageBox.top + stage.y() + cy * scale;
        let initial = '';
        try {
          initial = (node?.attrs?.link || '') as string;
        } catch {}
        setLinkPrompt({ visible: true, position: { x: px, y: py }, nodeId: targetId, initial });
      } catch (err) {
        canvasLogger.error('Failed to open link prompt', err);
      }
    });
    return () => off();
  }, [eventBus, nodes, selectedIds, nodeRefs, stageRef]);

  return { popupStyle, linkPrompt, setLinkPrompt, insertBetweenPicker, setInsertBetweenPicker };
}
